import { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageCircle, X, Send } from 'lucide-react';
import { colors } from './index';
import { usePrompts } from '../../contexts/PromptContext';

export default function FeedbackWidget({
  promptId,
  product,
  contextId,
  contextType,
  inputSnapshot,
  outputSnapshot,
  raterRole = 'user',
  compact = false,
  onFeedbackSubmitted,
}) {
  const { submitFeedback } = usePrompts();
  const [rating, setRating] = useState(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleRating = async (newRating) => {
    setRating(newRating);
    setSubmitting(true);

    try {
      await submitFeedback(promptId, {
        rating: newRating,
        product,
        contextId,
        contextType,
        ratedBy: 'anonymous', // Would use actual user ID
        raterRole,
        comment: null,
        inputSnapshot,
        outputSnapshot,
      });

      // Show comment option for negative feedback
      if (newRating === 'down') {
        setShowComment(true);
      } else {
        setSubmitted(true);
        onFeedbackSubmitted?.({ rating: newRating });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!comment.trim()) {
      setSubmitted(true);
      setShowComment(false);
      return;
    }

    setSubmitting(true);
    try {
      // Update feedback with comment
      await submitFeedback(promptId, {
        rating,
        product,
        contextId,
        contextType,
        ratedBy: 'anonymous',
        raterRole,
        comment: comment.trim(),
        inputSnapshot,
        outputSnapshot,
      });

      setSubmitted(true);
      setShowComment(false);
      onFeedbackSubmitted?.({ rating, comment: comment.trim() });
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={`flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm'}`}
        style={{ color: colors.textMuted }}
      >
        <span>Thanks for the feedback!</span>
      </div>
    );
  }

  if (showComment) {
    return (
      <div className="mt-2 p-3 rounded-lg" style={{ background: colors.inputBg }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm" style={{ color: colors.textSecondary }}>
            What could be improved?
          </span>
          <button
            onClick={() => {
              setShowComment(false);
              setSubmitted(true);
            }}
            style={{ color: colors.textMuted }}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional feedback..."
            className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: colors.cardBg, color: colors.textPrimary }}
            onKeyDown={(e) => e.key === 'Enter' && handleCommentSubmit()}
            autoFocus
          />
          <button
            onClick={handleCommentSubmit}
            disabled={submitting}
            className="p-2 rounded-lg"
            style={{ background: colors.yellowDim, color: colors.yellow }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'mt-2'}`}>
      <span
        className={`${compact ? 'text-xs' : 'text-sm'}`}
        style={{ color: colors.textMuted }}
      >
        Was this helpful?
      </span>
      <button
        onClick={() => handleRating('up')}
        disabled={submitting}
        className={`p-1.5 rounded-lg transition-all ${
          rating === 'up' ? '' : 'opacity-60 hover:opacity-100'
        }`}
        style={{
          background: rating === 'up' ? colors.greenDim : 'transparent',
          color: rating === 'up' ? colors.green : colors.textMuted,
        }}
      >
        <ThumbsUp size={compact ? 14 : 16} />
      </button>
      <button
        onClick={() => handleRating('down')}
        disabled={submitting}
        className={`p-1.5 rounded-lg transition-all ${
          rating === 'down' ? '' : 'opacity-60 hover:opacity-100'
        }`}
        style={{
          background: rating === 'down' ? colors.redDim : 'transparent',
          color: rating === 'down' ? colors.red : colors.textMuted,
        }}
      >
        <ThumbsDown size={compact ? 14 : 16} />
      </button>
    </div>
  );
}
